/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2023 (original work) Open Assessment Technologies SA ;
 */

import { tick } from 'svelte';
import pluginFactory from 'taoTests/runner/plugin';
import { testSessionStatus } from '../../../session/sessionStates.js';
import { getTestSessionStatusStore } from '../../../testsStateStore.js';
import TestTitle from './TestTitle.svelte';
import HiddenContentTitle from './HiddenContentTitle.svelte';
import TimersAriaLive from './TimersAriaLive.svelte';
import { getNavigationFeedbacksStore } from '../../../feedback';

const defaultThrottleConfig = {
    minutesThreshold: 10
};

/**
 * This plugin generates a contextual titles
 */
export default pluginFactory({
    name: 'title',

    init() {
        //mandatory
    },

    /**
     * Render the TestTitle component inside the Header Area
     */
    render() {
        const testRunner = this.getTestRunner();
        const areaBroker = testRunner.getAreaBroker();
        const testConfig = testRunner.getConfig();
        const serviceCallId = testConfig.serviceCallId;
        const titles = (testConfig.options || {}).titles;
        const navigationFeedbacksStore = getNavigationFeedbacksStore(serviceCallId);

        //render the title
        this.testTitle = new TestTitle({
            target: areaBroker.getHeaderArea(),
            props: {
                serviceCallId,
                titles
            }
        });

        //update the hidden title
        this.hiddenContentTitle = new HiddenContentTitle({
            target: areaBroker.getContainer().querySelector('#a11y-main'),
            props: {
                serviceCallId
            }
        });

        this.timersAriaLive = new TimersAriaLive({
            target: areaBroker.getHeaderArea(),
            props: {
                serviceCallId,
                throttleConfig: testConfig.options?.timersService?.throttleConfig || defaultThrottleConfig
            }
        });

        //focus hidden title on certain session status changes
        this.unsubscribeSessionStatus = getTestSessionStatusStore(testConfig.serviceCallId).subscribe(sessionStatus => {
            tick().then(() => {
                if (sessionStatus === testSessionStatus.interacting && !navigationFeedbacksStore.isAnyShown()) {
                    areaBroker.getContainer().querySelector('#a11y-main').focus();
                }
            });
        });
    },

    /**
     * Destroys the plugin
     */
    destroy() {
        if (this.unsubscribeSessionStatus) {
            this.unsubscribeSessionStatus();
            this.unsubscribeSessionStatus = null;
        }
        if (this.hiddenContentTitle) {
            this.hiddenContentTitle.$destroy();
        }
        if (this.testTitle) {
            this.testTitle.$destroy();
        }
        if (this.timersAriaLive) {
            this.timersAriaLive.$destroy();
        }
    }
});
